import { ofType } from "redux-observable";
import { createSelector } from "reselect";
import { filter, map, switchMap } from "rxjs/operators";
import JobProApi from "../../../dependencies/JobProApi";
import { updateEvent, splitEventExplicit } from "../../calendar";
import { updateJob } from "../../jobs";
import moment from "moment";

// Action types
const EDIT_EVENT_MODAL_UPDATE = "EDIT_EVENT_MODAL_UPDATE";
const EDIT_MODAL_SPLIT_CONFIRM = "EDIT_MODAL_SPLIT_CONFIRM";
const EDIT_MODAL_CONFIRM = "EDIT_MODAL_CONFIRM";
const FETCH_EDIT_MODAL_COMPLETE = "FETCH_EDIT_MODAL_COMPLETE";
const TOGGLE_EDIT_MODAL = "TOGGLE_EDIT_MODAL";
const TOGGLE_EDIT_CREW = "TOGGLE_EDIT_CREW";
const TOGGLE_APPT_EDIT_CREW = "TOGGLE_APPT_EDIT_CREW";

const DEFAULT_STATE = {
  equipment: [],
  jobCrews: [],
  form: {},
  isEditCrewEnabled: false,
  isApptEditCrewEnabled: false,
  event: null,
  loading: false,
  resources: [],
  show: false,
};

// Reducer
export default function (state = DEFAULT_STATE, { payload, type }) {
  switch (type) {
    case EDIT_EVENT_MODAL_UPDATE:
      return {
        ...state,
        form: {
          ...state.form,
          ...payload,
        },
      };
    case FETCH_EDIT_MODAL_COMPLETE:
      return {
        ...state,
        ...payload,
        form: payload
          ? {
              ...payload.event,
              jobStatus: payload.event.job ? payload.event.job.status : "",
              jobPriorityNotes: payload.event.job
                ? payload.event.job.priorityNotes
                : "",
            }
          : {},
        loading: false,
      };
    case TOGGLE_EDIT_MODAL:
      return payload.show
        ? {
            ...state,
            loading: payload.show,
            show: payload.show,
          }
        : DEFAULT_STATE;
    case TOGGLE_EDIT_CREW:
      return {
        ...state,
        isEditCrewEnabled: !state.isEditCrewEnabled,
      };
    case TOGGLE_APPT_EDIT_CREW:
      return {
        ...state,
        isApptEditCrewEnabled: !state.isApptEditCrewEnabled,
      };

    default:
      return state;
  }
}

// Action creators
export const editEventModalUpdate = (changes) => ({
  type: EDIT_EVENT_MODAL_UPDATE,
  payload: changes,
});

export const editModalSplitConfirm = () => ({
  type: EDIT_MODAL_SPLIT_CONFIRM,
});

export const editModalConfirm = () => ({
  type: EDIT_MODAL_CONFIRM,
});

export const toggleEditCrew = () => ({
  type: TOGGLE_EDIT_CREW,
});

export const toggleApptEditCrew = () => ({
  type: TOGGLE_APPT_EDIT_CREW,
});

export const toggleEditModal = (eventId) => ({
  type: TOGGLE_EDIT_MODAL,
  payload: {
    eventId,
    show: !!eventId,
  },
});

const fetchEditModalComplete = (payload) => ({
  type: FETCH_EDIT_MODAL_COMPLETE,
  payload,
});

// Epics
export const confirmEditModalEpic = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_MODAL_CONFIRM),
    map(() => {
      const { event, form, isEditCrewEnabled, jobCrews } =
        state$.value.modals.editModal;
      const {
        crew,
        description,
        endDate,
        endTime,
        jobStatus,
        startDate,
        startTime,
        jobPriorityNotes,
      } = form;

      return async (dispatch) => {
        // Update crew (if needed)
        console.log(
          crew,
          isEditCrewEnabled,
          event.job,
          jobCrews.some((x) => x.id === (crew ? crew.id : 0))
        );
        const isCrewUpdateNeeded =
          crew &&
          isEditCrewEnabled &&
          event.job &&
          jobCrews.some((x) => x.id === (crew ? crew.id : 0));
        if (
          isCrewUpdateNeeded &&
          state$.value.settings.schedulerView != "equipment"
        ) {
          await JobProApi.jobs.crews.put(event.job.id, crew.id, {
            equipment: crew.equipment ? { id: crew.equipment.id } : null,
            resources: crew.resources.map(
              ({ resource, resourcePercentage }) => ({
                resource: { id: resource.id },
                resourcePercentage,
              })
            ),
          });
        }

        if (
          isCrewUpdateNeeded &&
          state$.value.settings.schedulerView == "equipment"
        ) {
          dispatch(
            updateEvent(event.id, {
              // Skip crew and update directly -
              crew: crew
                ? {
                    equipment: crew.equipment
                      ? { id: crew.equipment.id }
                      : null,
                    resources: crew.resources
                      ? crew.resources
                          .filter((r) => !!r.resource)
                          .map((r) => ({
                            resource: { id: r.resource.id },
                            resourcePercentage: r.resourcePercentage,
                          }))
                      : [],
                  }
                : null,
              description,
              endDate,
              endTime,
              startDate,
              startTime,
              job: event.job,
            })
          );
        }
        // Update calendar event
        else if (
          isCrewUpdateNeeded &&
          state$.value.settings.schedulerView != "equipment"
        ) {
          dispatch(
            updateEvent(event.id, {
              // Skip crew and update directly
              description,
              endDate,
              endTime,
              startDate,
              startTime,
              job: event.job,
            })
          );
        } else {
          dispatch(
            updateEvent(event.id, {
              crew: crew
                ? {
                    equipment: crew.equipment
                      ? { id: crew.equipment.id }
                      : null,
                    resources: crew.resources.map(
                      ({ resource, resourcePercentage }) => ({
                        resource: { id: resource.id },
                        resourcePercentage,
                      })
                    ),
                  }
                : null,
              description,
              endDate,
              endTime,
              startDate,
              startTime,
              job: event.job,
            })
          );
        }

        // Update job status
        if (event.job) {
          dispatch(
            updateJob(event.job.id, {
              status: jobStatus.value,
              priorityNotes: jobPriorityNotes,
            })
          );
        }

        // Close the modal
        dispatch(toggleEditModal());
      };
    })
  );

export const toggleEditModalEpic = (action$) =>
  action$.pipe(
    ofType(TOGGLE_EDIT_MODAL),
    filter(({ payload }) => payload.show),
    switchMap(({ payload }) => {
      const { eventId } = payload;

      const calendarPromise = JobProApi.calendar.get(eventId, {
        fields:
          "id,crew(id),job(id,jobNumber,name,office(id),status,priorityNotes),description,startDate,endDate,startTime,endTime",
      });

      const equipmentPromise = JobProApi.equipment.query(
        {
          schedulable: true,
          "status!": "Inactive",
        },
        {
          fields: "id,name",
          limit: 9999,
        }
      );

      const jobCrewsPromise = calendarPromise.then((event) => {
        return event.job
          ? JobProApi.jobs.crews.query(event.job.id, {
              fields:
                "id,equipment(id,color,name),resources(resource(id,firstName,lastName),resourcePercentage)",
            })
          : Promise.resolve([]);
      });

      const resourcesPromise = JobProApi.resources.query(
        {
          isActive: true,
          isJobAssignee: true,
        },
        {
          fields: "id,calendarColor,firstName,lastName",
        }
      );

      return Promise.all([
        calendarPromise,
        equipmentPromise,
        jobCrewsPromise,
        resourcesPromise,
      ]).then(([event, equipment, jobCrews, resources]) => ({
        equipment,
        event: {
          ...event,
          crew: event.crew
            ? jobCrews.find((c) => c.id === event.crew.id)
            : null,
        },
        jobCrews,
        resources,
      }));
    }),
    map(fetchEditModalComplete)
  );

export const confirmSplitEditModalEpic = (action$, state$) =>
  action$.pipe(
    ofType(EDIT_MODAL_SPLIT_CONFIRM),
    map(() => {
      return async (dispatch) => {
        const { event, form } = state$.value.modals.editModal;
        const { id } = event;
        const { startDate, endDate } = form;

        // Check if start and end dates span at least two days
        const start = new Date(startDate);
        const end = new Date(endDate);

        console.log("hit", id, startDate, endDate);

        if (moment(start).isSame(end)) {
          alert(
            "To split this event, the start and end dates must span at least two days."
          );
          return; // Don't proceed with the operation
        }

        // Show confirmation dialog
        const confirmed = window.confirm(
          "Are you sure you want this to be converted into individual, daily events? This cannot be undone."
        );

        if (!confirmed) {
          return; // User cancelled the operation
        }

        dispatch(splitEventExplicit(event.id, startDate));

        // Close the modal
        dispatch(toggleEditModal());
      };
    })
  );

// Selectors
const getEventOffice = (event) => {
  if (!event) {
    return null;
  } else if (event.purchaseOrder) {
    return event.purchaseOrder.office;
  } else if (event.job) {
    return event.job.office;
  } else {
    return null;
  }
};

const getEventStandardCrewFilter = (event) => {
  if (!event) {
    return (c) => true;
  } else if (event.purchaseOrder) {
    return (c) => c.deliveryCrew;
  } else if (event.job) {
    return (c) => c.installationCrew;
  } else {
    return (c) => true;
  }
};

const standardCrewsSelector = createSelector(
  (state) => state.standardCrews.crews,
  (state) => state.modals.editModal.event,
  (standardCrews, event) => {
    const office = getEventOffice(event);
    return standardCrews.filter(getEventStandardCrewFilter(event)).map((c) => ({
      ...c,
      disabled: !!office && !!c.office && c.office.id !== office.id,
    }));
  }
);

export const editModalSelector = (state) => ({
  ...state.modals.editModal,
  jobStatuses: state.dropdowns.jobStatuses,
  standardCrews: standardCrewsSelector(state),
});
